import { StoryblokComponent, storyblokEditable } from "@storyblok/react/rsc";
import { clsx } from "clsx";
import { isEqual, map, sortBy, uniqWith } from "lodash";
import { useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import type {
  CampsiteStoryblok,
  OfferOverviewStoryblok,
} from "@/component-types-sb";
import { Card } from "@/components/layout/Card";
import { BodyText, Divider, H2 } from "@/components/typography";
import { isWithinStoryblokSchedule } from "@/lib/storyblok/utils";
import { normalizeSlug } from "@/lib/utils/slug";

type OfferOverviewProps = {
  blok: OfferOverviewStoryblok;
  searchParams?: { [key: string]: string | string[] | undefined };
};

const OfferOverview = ({ blok, searchParams }: OfferOverviewProps) => {
  const campsites: CampsiteStoryblok[] = map(
    blok.offers,
    "content.campsites"
  ).flat();
  const uniqueCampsites = uniqWith(campsites, isEqual);
  const sortedCampsites = sortBy(uniqueCampsites, ["name"]);
  const router = useRouter();
  const pathname = usePathname();
  const currentSearchParams = useSearchParams();
  const queryCampsite = currentSearchParams.get("campsite");
  const searchParamCampsite = searchParams?.campsite;
  const initialCampsite = Array.isArray(searchParamCampsite)
    ? searchParamCampsite[0]
    : searchParamCampsite || queryCampsite;
  const [selectedCampsiteFilter, setSelectedCampsiteFilter] = useState<
    string | null
  >(normalizeSlug(initialCampsite) || null);
  const selectedCampsite = selectedCampsiteFilter
    ? sortedCampsites.find(
        (campsite) =>
          normalizeSlug(campsite.full_slug || campsite.slug) ===
            normalizeSlug(selectedCampsiteFilter)
      ) ||
      null
    : null;

  const handleSelectCampsite = (campsite: CampsiteStoryblok | null) => {
    const campsiteSlug = normalizeSlug(campsite?.full_slug || campsite?.slug);
    setSelectedCampsiteFilter(campsiteSlug || null);

    const params = new URLSearchParams(currentSearchParams);
    if (campsiteSlug) {
      params.set("campsite", campsiteSlug);
    } else {
      params.delete("campsite");
    }

    const queryString = params.toString();
    router.replace(queryString ? `${pathname}?${queryString}` : pathname, {
      scroll: false,
    });
  };

  const shouldRenderOffer = (offer: any) => {
    const matchesCampsite = selectedCampsite
      ? offer.content?.campsites
          ?.map((campsite: CampsiteStoryblok) => campsite.uuid)
          .includes(selectedCampsite?.uuid)
      : true;

    const withinSchedule = isWithinStoryblokSchedule(
      offer.content?.publishDate,
      offer.content?.expireDate
    );

    return matchesCampsite && withinSchedule;
  };

  return (
    <article className="w-full" {...storyblokEditable(blok)}>
      <header>
        {blok.header?.map((nestedBlok) => (
          <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
        ))}
      </header>

      <section className="py-8 mx-2 lg:mx-0 text-center">
        <H2 colorScheme="primary">{blok.title}</H2>
        <BodyText className="max-w-md mx-auto mt-2 text-center">
          {blok.description}
        </BodyText>

        <Divider />

        <div className="max-w-5xl mx-auto mb-24 grid gap-8 text-left">
          <div className="snap-x scroll-px-4 snap-mandatory scroll-smooth flex md:flex-wrap justify-left md:justify-center gap-3 mx-4 mb-8 pb-4 md:pb-0 overflow-x-auto">
            <button
              className={clsx(
                "bg-white snap-start shrink-0 py-1 px-2 border text-sm hover:border-brand-primary100 hover:text-brand-primary100",
                {
                  ["border-brand-primary100 text-brand-primary100 font-medium"]:
                    !selectedCampsite,
                  ["border-gray-400"]: selectedCampsite,
                }
              )}
              onClick={() => {
                handleSelectCampsite(null);
              }}
            >
              Alle Campingparks
            </button>

            {sortedCampsites?.map((campsite, index: number) => (
              <button
                key={index}
                className={clsx(
                  "bg-white snap-start shrink-0 py-1 px-2 border text-sm hover:border-brand-primary100 hover:text-brand-primary100 shadow-sm",
                  {
                    ["border-brand-primary100 text-brand-primary100 font-medium"]:
                      campsite.uuid === selectedCampsite?.uuid,
                    ["border-gray-400"]:
                      campsite.uuid !== selectedCampsite?.uuid,
                  }
                )}
                onClick={() => {
                  handleSelectCampsite(campsite);
                }}
              >
                {campsite.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {blok?.offers
              ?.filter(shouldRenderOffer)
              .map((nestedBlok: any) => (
                <Card
                  key={nestedBlok.id}
                  image={nestedBlok.content?.image}
                  href={nestedBlok.full_slug}
                  name={nestedBlok.name}
                  text={nestedBlok.content?.subline}
                  buttonLabel="Mehr erfahren"
                />
              ))}
          </div>
        </div>
      </section>
    </article>
  );
};

export default OfferOverview;
